import React, { useEffect, useMemo, useState } from 'react';
import ReconciliationMachineFormAccordionContent from '../ReconciliationDetail/ReconciliationMachineFormAccordionContent';
import ReactTable from '../../../components/shared/ReactTable';
import { COLUMNS } from '../../../constants/tableColumns';
import { InformationField, Accordion } from '../../../components/shared';
import MachineFormAccordionContent from './MachineFormAccordionContent';
import ApprovedMachineFormAccordionContent from '../ApprovedCollectionDetail/ApprovedMachineFormAccordionContent';
import { MESSAGE } from '../../../constants/message';
import { Box, Button, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  getApprovedAssetSubtotalMap,
  getPlayCardApiSchemaData,
  hasLegacyCreditCardRow,
  updateMeterNameForReconciliation,
} from '../../../utils/collection-methods';
import PlayCardSummary from './PlayCardSummary';
import {
  checkValueNotNullUndefinedBlank,
  isAllObjValuesNullOrEmpty,
  getDateFormat,
  isAllObjectValuesNullOrZero,
  isBothObjectSame,
} from '../../../utils/common-methods';
import { setRerender } from '../../../redux-slice/reconciliationCollection';
import useStyles from './style';
import { useFormikContext } from 'formik';
import AccountSummary from './AccountSummary';
import { CURRENCY_CONSTANT, DEFAULT_EXCHANGE_RATE } from '../../../constants/CurrencyConstants';
import SublocationDateModule from './SublocationDateModule';
import ApprovedSublocationDateModule from '../ApprovedCollectionDetail/ApprovedSublocationDateModule';
import InPageNotification from '../../../components/InPageNotification';
import { IN_PAGE_NOTIFICATION_TYPES as NOTIFICATION_TYPE } from '../../../constants/InPageNotificationTypes';
import { getLastCollectionDate } from '../../../utils/collection-methods.js';
import { ROLE } from '../../../constants/roles';
import { useHasRoles } from '../../../hooks';
import AddMissingAssets from './AddMissingAssets.jsx';
import IndependentRepUserDeatils from './IndependentRepUserDetails.jsx';
import AccountSummaryNew from './AccountSummaryNew.jsx';
import { getApprovedErrorAndMessage } from '../../../utils/approved-warning-methods';
import { FIELD_NAME, METER_TYPE, METER_TYPE_FILED_MAP } from '../../../constants/MeterTypeFieldMap';
import { getAssetHasReconciliationVarianceWarning } from '../../../utils/reconciliation-childField-methods';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const assetFieldBelongsToIds = (fieldKey = '', sublocationId, assetId) =>
  String(fieldKey).includes(`_${sublocationId}_${assetId}`);

const VARIANCE_WARNING_MESSAGES = new Set([
  MESSAGE.CASH_METER_EXPECTED_REVENUE_ERROR,
  MESSAGE.CREDIT_METER_ESTIMATED_REVENUE_ERROR,
  MESSAGE.COIN_ESTIMATED_COIN_REVENUE_ERROR,
  MESSAGE.TOKEN_CHANGER_METER_EXPECTED_REVENUE_ERROR,
]);

const isVarianceWarningField = (fieldKey = '') =>
  (fieldKey.includes(FIELD_NAME.BILLS_COLLECTED) && fieldKey.includes(METER_TYPE.CASH)) ||
  (fieldKey.includes(FIELD_NAME.COINS_COLLECTED) &&
    (fieldKey.includes(METER_TYPE.CASH) || fieldKey.includes(METER_TYPE.COIN))) ||
  (fieldKey.includes(FIELD_NAME.METER_ADJUST) && fieldKey.includes(METER_TYPE.CASH)) ||
  ((fieldKey.includes(FIELD_NAME.EXPECTED_REVENUE) || fieldKey.includes(FIELD_NAME.SEEDLIVE_REVENUE)) &&
    fieldKey.includes(METER_TYPE.CREDIT)) ||
  (fieldKey.includes(FIELD_NAME.REVENUE_COLLECTED_FROM_TOKEN_CHANGER) && fieldKey.includes(METER_TYPE.TOKEN_CHANGER));

const getApprovedAssetHasVarianceWarning = (asset, exchangeRate) => {
  if (!Array.isArray(asset?.assetMeters) || asset.assetMeters.length === 0) {
    return false;
  }

  return asset.assetMeters.some((assetMeter) => {
    const meterTypeName = updateMeterNameForReconciliation(assetMeter?.meter?.name, true);
    const fields = METER_TYPE_FILED_MAP[meterTypeName] || [];

    return fields.some((meterMapElement) => {
      const { error, message } = getApprovedErrorAndMessage({
        meterMapElement,
        assetMeter,
        clicksPerPlay: asset?.clicksPerPlay,
        costPerPlay: asset?.costPerPlay,
        meterTypeName,
        exchangeRate,
      });

      return error && VARIANCE_WARNING_MESSAGES.has(message);
    });
  });
};

const SublocationMachinesForm = ({
  averageTokenValue = 0,
  sublocation: onMountSublocation,
  originalSublocation,
  isReconciliation,
  isVerified = false,
  isApproved,
  readFromDraft,
  transactionId,
  turnOffDraft,
  locationCurrency = {},
  searchState,
  sortState = false,
  onDelete,
  handleSublocationDeleteClick,
  saveAsDraftHandler,
  onSublocationAssetsUpdate,
  onWarningChange,
}) => {
  const isSuperAdmin = useHasRoles([ROLE.SUPER_ADMIN_IT]);
  const hasDeleteSublocationAndAssetAccess = useHasRoles([ROLE.SUPER_ADMIN_IT, ROLE.ACCOUNTING]);
  const [sublocation, setSublocation] = useState({});
  const [skip, setSkip] = useState(true);
  const [resetSummary, setResetSummary] = useState(readFromDraft);
  const { reconciliationWarningForModal, intercardDataForReconciliation, reRender } = useSelector(
    (state) => state.reconciliationCollection,
  );
  useEffect(() => setSublocation({ ...onMountSublocation }), [onMountSublocation, reRender]);
  const assets = useMemo(() => sublocation?.assets, [sublocation?.assets, reRender]);
  const [assetSubtotalMap, setAssetSubtotalMap] = useState({});
  const [isSublocationDirty, setIsSublocationDirty] = useState(false);
  const classes = useStyles();
  const isManagerUser = useHasRoles([ROLE.MANAGER, ROLE.DM, ROLE.REGIONAL_MANAGER, ROLE.EXECS, ROLE.VP]);
  const isAccountingAdminUser = useHasRoles([ROLE.ACCOUNTING, ROLE.SUPER_ADMIN_IT]);
  const dispatch = useDispatch();
  //The below useEffect required control drafted account summary
  useEffect(() => setResetSummary(readFromDraft), [readFromDraft]);
  useEffect(() => {
    if (reRender) {
      setTimeout(() => {
        dispatch(setRerender(false));
      });
    }
  }, [reRender, dispatch]);
  const handleDeleteClick = (row) => {
    if (onDelete) {
      onDelete(row); // Pass sublocation ID or any other identifier
    }
  };

  const collectionAssetExpandColumn = useMemo(
    () => ({
      Header: () => null,
      id: 'expander',
      isPadding: true,
      padding: '6px 0px 6px 8px',
      width: 44,
      Cell: ({ row }) => {
        const handleToggleAsset = (event) => {
          event.preventDefault();
          event.stopPropagation();
          row.toggleRowExpanded?.(!row.isExpanded);
        };

        return (
          <button
            type="button"
            onClick={handleToggleAsset}
            data-row-expander="true"
            data-testid="expandIcon"
            aria-label={row.isExpanded ? 'Collapse asset details' : 'Expand asset details'}
            style={{
              width: '32px',
              minWidth: '32px',
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 0,
              borderRadius: '999px',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            {row.isExpanded ? <ExpandMore /> : <KeyboardArrowRight />}
          </button>
        );
      },
    }),
    [],
  );

  const assetColumn =
    isReconciliation || isApproved
      ? isApproved
        ? COLUMNS.RECONCILIATION_APPROVED_ACCORDION_ASSET_TABLE
        : [...COLUMNS.RECONCILIATION_APPROVED_ACCORDION_ASSET_TABLE, ...COLUMNS.CREATE_COLLECTION_ASSET_TABLE]
      : COLUMNS.CREATE_COLLECTION_ASSET_TABLE;
  const columns =
    hasDeleteSublocationAndAssetAccess && isReconciliation
      ? [
          collectionAssetExpandColumn,
          ...COLUMNS.RECONCILIATION_ACCORDION_ASSET_TABLE,
          ...assetColumn,
          COLUMNS.ASSET_WARNING_COLUMN,

          {
            Header: () => null,
            id: 'deleteAsset',
            Cell: ({ row }) => <span>{<Button onClick={() => handleDeleteClick(row.original)}>delete</Button>}</span>,
            width: 56,
          },
        ]
      : [
          collectionAssetExpandColumn,
          ...COLUMNS.RECONCILIATION_ACCORDION_ASSET_TABLE,
          ...assetColumn,
          COLUMNS.ASSET_WARNING_COLUMN,
        ];
  const { playCards } = useSelector((state) => state.playCard);
  const { errors, touched, initialValues, values } = !isApproved ? useFormikContext() : {};
  const reconciliationWarningEntries = useMemo(
    () => Object.entries(reconciliationWarningForModal || {}),
    [reconciliationWarningForModal],
  );

  const assetWarnings = useMemo(() => {
    const exchangeRate = locationCurrency?.exchangeRate || DEFAULT_EXCHANGE_RATE;

    return (assets || []).reduce((warningMap, asset) => {
      const sublocationId = asset?.subLocationId ?? sublocation?.id;
      const assetId = asset?.id;
      const hasReconciliationFieldVarianceWarnings = isReconciliation
        ? getAssetHasReconciliationVarianceWarning({
            asset,
            values,
            exchangeRate,
            sublocationId,
          })
        : false;
      const hasReconciliationVarianceWarnings = reconciliationWarningEntries.some(
        ([fieldKey, hasWarning]) =>
          Boolean(hasWarning) &&
          assetFieldBelongsToIds(fieldKey, sublocationId, assetId) &&
          isVarianceWarningField(fieldKey),
      );
      const hasApprovedVarianceWarnings = isApproved ? getApprovedAssetHasVarianceWarning(asset, exchangeRate) : false;

      warningMap[assetId] = Boolean(
        hasReconciliationFieldVarianceWarnings || hasReconciliationVarianceWarnings || hasApprovedVarianceWarnings,
      );

      return warningMap;
    }, {});
  }, [assets, reconciliationWarningEntries, isApproved, isReconciliation, locationCurrency?.exchangeRate, values]);

  const hasSublocationWarning = useMemo(() => Object.values(assetWarnings).some(Boolean), [assetWarnings]);

  useEffect(() => {
    if (typeof onWarningChange === 'function' && sublocation?.id != null) {
      onWarningChange(sublocation.id, hasSublocationWarning, assetWarnings);
    }
  }, [onWarningChange, sublocation?.id, hasSublocationWarning, assetWarnings]);

  useEffect(
    () => () => {
      if (typeof onWarningChange === 'function' && sublocation?.id != null) {
        onWarningChange(sublocation.id, false, {});
      }
    },
    [onWarningChange, sublocation?.id],
  );

  const renderAccordionTitle = (subComponent = null) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Box display="flex" alignItems="center">
        <Typography ml={2} data-testid="accordionTitle" variant="subtitle5">
          {sublocation?.name}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center">
        {subComponent}
      </Box>
    </Box>
  );

  // transformed assets
  const data = useMemo(
    () =>
      assets?.map((item) => {
        const lastCollectionDate = item?.lastCollectionByAsset?.dateOfReading;
        let sublocationLastCollection = getLastCollectionDate(sublocation, isReconciliation);
        const lastCollectionForSubloc = checkValueNotNullUndefinedBlank(sublocationLastCollection)
          ? getDateFormat(sublocationLastCollection)
          : 'N/A';
        const lastCollection = !lastCollectionDate
          ? lastCollectionForSubloc || 'N/A'
          : getDateFormat(item?.lastCollectionByAsset?.dateOfReading);
        let updateData = { ...item, lastCollection };
        if (isReconciliation || isApproved) {
          const costPerPlay = item.costPerPlay || '0.00';
          const clicksPerPlay = item.clicksPerPlay || 0;
          updateData.costPerPlay = costPerPlay;
          updateData.clicksPerPlay = clicksPerPlay;
        }
        if (
          isReconciliation &&
          intercardDataForReconciliation &&
          intercardDataForReconciliation[item?.id] &&
          intercardDataForReconciliation[item?.id].intercardData
        ) {
          updateData.intercardTransactions = intercardDataForReconciliation[item?.id].intercardData;
        }
        updateData.hasWarning = assetWarnings[item?.id] ?? false;
        updateData.scrollId = `asset-${sublocation?.id}-${item?.id}`;
        return updateData;
      }),
    [assets, isReconciliation, isApproved, intercardDataForReconciliation, reRender, assetWarnings],
  );

  const getRowErrorClass = (errors, rowData, touched) => {
    const warningErrors = {};
    for (const key in reconciliationWarningForModal) {
      if (reconciliationWarningForModal[key] === true) {
        if (
          !key.includes('Token changer_revenueCollectedFromTokenChanger') &&
          !key.includes('Credit_expectedRevenue')
        ) {
          warningErrors[key] = reconciliationWarningForModal[key];
        }
      }
    }
    return Object.keys({ ...warningErrors, ...errors } || {})
      .join(' ')
      .includes(rowData?.original?.id) && Object.values(touched)?.length
      ? ' error'
      : '';
  };

  useEffect(() => {
    const abbreviation = locationCurrency?.abbreviation || CURRENCY_CONSTANT.USD;
    const exchangeRate = locationCurrency?.exchangeRate || DEFAULT_EXCHANGE_RATE;
    if (isApproved) {
      const hasLegacyCC = hasLegacyCreditCardRow(onMountSublocation?.accountSumDetails);
      const isStandalone = Boolean(sublocation?.calculateRent) || hasLegacyCC;
      const subtotalMap = getApprovedAssetSubtotalMap(
        assets,
        abbreviation,
        exchangeRate,
        isApproved,
        isStandalone,
        sublocation?.creditCashControl,
      );
      setAssetSubtotalMap(subtotalMap);
    }
  }, [isApproved, locationCurrency, assets, sublocation?.calculateRent, onMountSublocation?.accountSumDetails]);

  /* We have used this hook to check if the sublocation have been updated or not. As we have location level check present and not sublocation level. */
  useEffect(() => {
    if (isReconciliation && sublocation?.id) {
      const sublocationInitialValues = Object.entries(initialValues).filter(([keys]) => {
        const sublocationId = keys.split('_')?.[2] || undefined;
        if (Number(sublocationId) === sublocation?.id) {
          return true;
        }
        return false;
      });
      const sublocationInitialValuesObject = Object.fromEntries(sublocationInitialValues);
      const sublocationValues = Object.entries(values).filter(([keys]) => {
        const sublocationId = keys.split('_')?.[2] || undefined;
        if (Number(sublocationId) === sublocation?.id) {
          return true;
        }
        return false;
      });
      const sublocationValuesObject = Object.fromEntries(sublocationValues);

      const isBothSame = isBothObjectSame(sublocationValuesObject, sublocationInitialValuesObject);
      setIsSublocationDirty(!isBothSame);
    }
  }, [values, readFromDraft, reRender]);

  const accordionComponent = ({ row }) => {
    const original = row?.original;
    if (isReconciliation) {
      return (
        <ReconciliationMachineFormAccordionContent
          asset={original}
          skip={skip}
          isStandaloneSublocation={Boolean(sublocation?.calculateRent)}
        />
      );
    }
    if (isApproved) {
      return (
        <ApprovedMachineFormAccordionContent
          asset={original}
          assetSubtotalMap={assetSubtotalMap}
          locationCurrency={locationCurrency}
          averageTokenValue={averageTokenValue}
          isStandaloneSublocation={
            Boolean(sublocation?.calculateRent) || hasLegacyCreditCardRow(onMountSublocation?.accountSumDetails)
          }
        />
      );
    }
    return <MachineFormAccordionContent asset={original} />;
  };

  const playCardsData = playCards[sublocation?.id];
  const playCardStandardData = getPlayCardApiSchemaData(playCardsData);
  const showPlayCardSummary =
    (sublocation && playCardsData && !isAllObjectValuesNullOrZero(playCardStandardData)) ||
    sublocation?.isIntercardSublocation;
  const showAccountSummary = isApproved
    ? onMountSublocation &&
      onMountSublocation?.accountSumDetails &&
      !isAllObjValuesNullOrEmpty(onMountSublocation?.accountSumDetails)
    : onMountSublocation &&
      onMountSublocation?.accountNumbers &&
      !isAllObjValuesNullOrEmpty(onMountSublocation?.accountNumbers);

  if (data?.length === 0 && !showPlayCardSummary && !showAccountSummary) {
    return (
      <Accordion
        customizedTitle
        defaultExpanded
        summaryTitleVariant="subtitle5"
        marginBottom={3}
        title={renderAccordionTitle()}
        content={<InformationField variant="subtitle1" value={MESSAGE.NO_ASSOCIATED_ASSET} />}
      />
    );
  }

  if (isReconciliation) {
    const isManagerUserAndSubmitted = isManagerUser && !isAccountingAdminUser;
    return (
      <div id={`sublocation-${sublocation?.id}`}>
        <Accordion
          customizedTitle
          accordionSummaryStyle
          noGutterAccordion
          summaryTitleVariant="subtitle5"
          defaultExpanded
          marginBottom={3}
          title={renderAccordionTitle(
            <SublocationDateModule
              sublocation={sublocation}
              setSkip={setSkip}
              setSublocation={setSublocation}
              skip={skip}
              isReconciliation={isReconciliation}
              originalSublocation={originalSublocation}
              readFromDraft={readFromDraft}
              turnOffDraft={turnOffDraft}
              hasWarning={hasSublocationWarning}
              handleSublocationDeleteClick={(id) => handleSublocationDeleteClick(id)}
              saveAsDraftHandler={saveAsDraftHandler}
              onSublocationAssetsUpdate={onSublocationAssetsUpdate}
            />,
          )}
          content={
            <Box className={classes.reconciliationAccordion}>
              <ReactTable
                data={data || []}
                columns={columns}
                expandable
                expandAllRowsByDefault
                expandRowOnClick
                renderRowSubComponent={accordionComponent}
                rowClassName={(rowData) => getRowErrorClass(errors, rowData, touched)}
              />
              {data?.length === 0 && (
                <Box padding={2}>
                  <InformationField variant="subtitle1" value={MESSAGE.NO_ASSOCIATED_ASSET} />
                </Box>
              )}
              <Box id={`sublocation-summary-${sublocation?.id}`}>
                {sublocation.exceptions?.length
                  ? sublocation.exceptions.map(
                      ({ message, displayWarning }, index) =>
                        displayWarning && (
                          <InPageNotification
                            key={`exceptionWarning${index}`}
                            type={NOTIFICATION_TYPE.WARNING}
                            message={message}
                          />
                        ),
                    )
                  : null}
                {isSuperAdmin && onMountSublocation.missingAssets?.length ? (
                  <AddMissingAssets
                    sublocationId={onMountSublocation.id}
                    missingAssets={onMountSublocation.missingAssets}
                  />
                ) : null}
                {showPlayCardSummary && (
                  <PlayCardSummary readFromDraft={readFromDraft} sublocation={sublocation} skip={skip} />
                )}
                {(!isManagerUserAndSubmitted && showAccountSummary) ||
                (sublocation && Object.keys(sublocation)?.length > 0) ? (
                  <Box className={classes.summaryAndRepRow}>
                    {!isManagerUserAndSubmitted && showAccountSummary && (
                      <Box className={classes.summaryAndRepPrimary}>
                        <AccountSummaryNew
                          sublocation={onMountSublocation}
                          locationCurrency={locationCurrency}
                          isVerified={isVerified}
                          isReconciliation={isReconciliation}
                          accountSummaryDefault={onMountSublocation?.accountSumDetails}
                          searchState={searchState}
                          formIsDirty={isSublocationDirty}
                          readFromDraft={resetSummary}
                          transactionId={transactionId}
                          setReadFromDraft={setResetSummary}
                          sortState={sortState}
                        />
                      </Box>
                    )}
                    {sublocation && Object.keys(sublocation)?.length > 0 && (
                      <Box className={classes.summaryAndRepSecondary}>
                        <IndependentRepUserDeatils
                          sublocation={sublocation}
                          showCustArCode={Boolean(sublocation?.calculateRent)}
                        />
                      </Box>
                    )}
                  </Box>
                ) : null}
              </Box>
            </Box>
          }
        />
      </div>
    );
  }

  if (isApproved) {
    return (
      <div id={`sublocation-${sublocation?.id}`}>
        <Accordion
          customizedTitle
          accordionSummaryStyle
          noGutterAccordion
          summaryTitleVariant="subtitle5"
          defaultExpanded
          title={renderAccordionTitle(
            <ApprovedSublocationDateModule sublocation={sublocation} hasWarning={hasSublocationWarning} />,
          )}
          content={
            <Box className={classes.reconciliationAccordion}>
              <ReactTable
                data={data || []}
                columns={columns}
                expandable
                expandAllRowsByDefault
                expandRowOnClick
                renderRowSubComponent={accordionComponent}
              />
              {data?.length === 0 && (
                <Box padding={2}>
                  <InformationField variant="subtitle1" value={MESSAGE.NO_ASSOCIATED_ASSET} />
                </Box>
              )}
              <Box id={`sublocation-summary-${sublocation?.id}`}>
                {showPlayCardSummary && <PlayCardSummary sublocation={sublocation} isApproved={isApproved} />}
                {showAccountSummary || (sublocation && Object.keys(sublocation)?.length > 0) ? (
                  <Box className={classes.summaryAndRepRow}>
                    {showAccountSummary && (
                      <Box className={classes.summaryAndRepPrimary}>
                        <AccountSummary
                          sublocation={onMountSublocation}
                          isApproved={isApproved}
                          accountSummaryDefault={onMountSublocation?.accountSumDetails}
                          locationCurrency={locationCurrency}
                          locationPaysSalesTax={onMountSublocation?.locationPaysSalesTax}
                        />
                      </Box>
                    )}
                    {sublocation && Object.keys(sublocation)?.length > 0 && (
                      <Box className={classes.summaryAndRepSecondary}>
                        <IndependentRepUserDeatils
                          sublocation={sublocation}
                          showCustArCode={Boolean(sublocation?.calculateRent)}
                        />
                      </Box>
                    )}
                  </Box>
                ) : null}
              </Box>
            </Box>
          }
        />
      </div>
    );
  }

  // Only Add new collection page can read from draft
  return (
    <div id={`sublocation-${sublocation?.id}`}>
      <Accordion
        customizedTitle
        accordionSummaryStyle
        noGutterAccordion
        summaryTitleVariant="subtitle5"
        defaultExpanded
        title={renderAccordionTitle(
          <SublocationDateModule
            sublocation={sublocation}
            originalSublocation={originalSublocation}
            setSkip={setSkip}
            setSublocation={setSublocation}
            skip={skip}
            hasWarning={hasSublocationWarning}
            readFromDraft={readFromDraft}
            turnOffDraft={turnOffDraft}
            saveAsDraftHandler={saveAsDraftHandler}
            onSublocationAssetsUpdate={onSublocationAssetsUpdate}
          />,
        )}
        content={
          <Box className={classes.openCollectionAccordion}>
            <ReactTable
              data={data || []}
              columns={columns}
              expandable
              expandAllRowsByDefault
              expandRowOnClick
              renderRowSubComponent={accordionComponent}
              rowClassName={(rowData) => getRowErrorClass(errors, rowData, touched)}
            />
            {data?.length === 0 && (
              <Box padding={2}>
                <InformationField variant="subtitle1" value={MESSAGE.NO_ASSOCIATED_ASSET} />
              </Box>
            )}
            <Box id={`sublocation-summary-${sublocation?.id}`}>
              {sublocation.exceptions?.length
                ? sublocation.exceptions.map(({ message }, index) => (
                    <InPageNotification key={`test${index}`} type={NOTIFICATION_TYPE.WARNING} message={message} />
                  ))
                : null}
              {showPlayCardSummary && (
                <PlayCardSummary sublocation={sublocation} readFromDraft={readFromDraft} skip={skip} />
              )}
            </Box>
          </Box>
        }
      />
    </div>
  );
};

export default React.memo(SublocationMachinesForm);
